/**
 * A2F-990 — pure helpers for active-org selection and stale-X-Org-Id recovery.
 *
 * Kept dependency-free (no DOM, no React, no api/supabase imports) so they
 * unit-test in the node vitest environment, and so the shipped code paths
 * (api.ts handleForbidden, OrgSelector) and the tests exercise the SAME logic.
 */

/** One of the user's org memberships, narrowed to the field these helpers need. */
export interface WebAppAccessLike {
  webAppAccess?: boolean;
}

/**
 * Whether a 403 message indicates the active-org selection (X-Org-Id) was
 * rejected for that org — SSO-required, not-a-member, or no web-app access.
 *
 * When this is true AND a selection was stored, `api.ts` clears
 * `chrona.active_org_id` so the next request falls back to the user's home
 * org instead of looping. The "web app access" arm is the A2F-990 addition:
 * a stale selection pointing at a ribbon/add-in-only membership 403s every
 * request (including /auth/me) with "...does not have web app access" /
 * "...not enabled for your organization", which the prior SSO/not-member
 * matching missed — leaving the user stranded.
 */
export function isOrgSwitchRejectionMessage(message: string): boolean {
  // Case-insensitive: the two web-app-access messages differ in casing
  // ("...does not have web app access" vs "Web app access is not enabled..."),
  // and lowercasing also hardens the SSO / not-member arms against future
  // backend copy drift.
  const m = message.toLowerCase();
  return (
    m.includes('requires sso')
    || m.includes('not a member of this organization')
    || m.includes('web app access')
  );
}

/**
 * Whether an org-switch 403 is specifically an SSO-required rejection — the
 * only case where offering a "Sign in with Microsoft" action can resolve it.
 *
 * `isOrgSwitchRejectionMessage` (above) clears the stale selection for ALL
 * org-switch rejections, but the not-a-member and no-web-app-access arms are
 * fixed by falling back to the home org, not by re-authenticating — surfacing
 * the Microsoft CTA for those would be misleading (A2F-990 review nit).
 */
export function isSsoRequiredMessage(message: string): boolean {
  return message.toLowerCase().includes('requires sso');
}

/**
 * Whether an org membership can be selected as the active org in the web app.
 *
 * False only when `webAppAccess` is explicitly `false` (a ribbon/add-in-only
 * membership, typically role=author). `undefined` — from /auth/me payloads
 * cached before the field existed — is treated as selectable so we never
 * wrongly disable an org the user can actually enter.
 */
export function canSelectOrgInWebApp(m: WebAppAccessLike): boolean {
  return m.webAppAccess !== false;
}
